import {
  Avatar,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useState } from 'react';
import type { Message as MessageType } from '../../../graphql/types';
import {
  getChatPreviewLinkBehavior,
  getMessagePreviews,
  openChatPreviewUrl,
} from '../../../utils/chatLinks';
import { getReadableShort } from '../../../utils/dates';
import { LinkPreview } from '../LinkPreview';

interface ChatMessagesProps {
  messages: MessageType[];
  loading: boolean;
  currentUserId: string;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onLinkClick: (url: string, messageId: string) => void;
}

// ─── Styled components ───────────────────────────────────────────────────────

const MessagesContainer = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: `${theme.baseUnit * 2}px ${theme.baseUnit * 2}px 0`,
  maxHeight: 400,
  display: 'flex',
  flexDirection: 'column',
}));

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 200,
});

const EmptyState = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 200,
  padding: theme.baseUnit * 3,
  textAlign: 'center',
}));

const shouldForwardIsCurrentUser = (prop: string) => prop !== 'isCurrentUser';

const MessageItem = styled('div', {
  shouldForwardProp: shouldForwardIsCurrentUser,
})<{ isCurrentUser: boolean }>(({ theme, isCurrentUser }) => ({
  display: 'flex',
  flexDirection: isCurrentUser ? 'row-reverse' : 'row',
  marginBottom: theme.baseUnit * 2,
  alignItems: 'flex-start',
  width: '100%',
}));

const shouldForwardMessageContent = (prop: string) =>
  prop !== 'isCurrentUser' && prop !== '$hasPreviews';

const MessageContent = styled('div', {
  shouldForwardProp: shouldForwardMessageContent,
})<{ isCurrentUser: boolean; $hasPreviews: boolean }>(
  ({ theme, isCurrentUser, $hasPreviews }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
    maxWidth: '70%',
    width: $hasPreviews ? '70%' : 'auto',
    marginLeft: isCurrentUser ? 0 : theme.baseUnit,
  }),
);

const MessageBubble = styled('div', {
  shouldForwardProp: shouldForwardIsCurrentUser,
})<{ isCurrentUser: boolean }>(({ theme, isCurrentUser }) => ({
  padding: theme.baseUnit * 1.5,
  borderRadius: theme.baseUnit * 2,
  backgroundColor: isCurrentUser
    ? theme.colors.alias.secondary
    : theme.colors.gray[100],
  color: theme.colors.alias.textPrimary,
  wordBreak: 'break-word',
}));

const MessageTextBlock = styled('div')(({ theme }) => ({
  ...theme.typography.fontFamily.reader400,
  fontSize: 14,
  lineHeight: '20px',
  wordBreak: 'break-word',
}));

const MessageTime = styled(Typography, {
  shouldForwardProp: (prop: string) => prop !== 'isCurrentUser',
})<{ isCurrentUser?: boolean }>(({ theme, isCurrentUser }) => ({
  ...theme.typography.fontFamily.reader400,
  fontSize: 11,
  color: theme.colors.gray[600],
  marginTop: theme.baseUnit / 2,
  textAlign: isCurrentUser ? 'right' : 'left',
}));

const MessageImages = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.baseUnit / 2,
}));

const MessageImage = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  height: 'auto',
  maxHeight: 150,
  borderRadius: theme.baseUnit,
  cursor: 'pointer',
  objectFit: 'contain',
  display: 'block',
}));

const DialogImageButton = styled('button')({
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  width: '100%',
  display: 'block',
});

const DialogImage = styled('img')({
  width: '100%',
  height: 'auto',
  display: 'block',
});

const shouldForwardShowBubble = (prop: string) => prop !== 'showBubble';

const PreviewsContainer = styled('div', {
  shouldForwardProp: shouldForwardShowBubble,
})<{ showBubble: boolean }>(({ theme, showBubble }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.baseUnit,
  marginTop: showBubble ? theme.baseUnit : 0,
  width: '100%',
}));

// ─── ImagePreviewDialog ───────────────────────────────────────────────────────

interface ImagePreviewDialogProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
}

const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  open,
  imageUrl,
  onClose,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogContent sx={{ padding: 0 }}>
      <DialogImageButton
        type="button"
        onClick={onClose}
        aria-label="Close full size preview"
      >
        <DialogImage src={imageUrl} alt="Full size preview" />
      </DialogImageButton>
    </DialogContent>
  </Dialog>
);

// ─── MessageItemComponent ────────────────────────────────────────────────────

interface MessageItemProps {
  message: MessageType;
  isCurrentUser: boolean;
  onLinkClick?: (url: string, messageId: string) => void;
}

const isImageUrl = (url: string) =>
  /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || url.includes('amazonaws.com');

const MessageItemComponent: React.FC<MessageItemProps> = ({
  message,
  isCurrentUser,
  onLinkClick,
}) => {
  const [previewImage, setPreviewImage] = useState('');

  const handleOpenLink = useCallback(
    (url: string) => {
      onLinkClick?.(url, message.id);
      openChatPreviewUrl(url, {
        openExternal: (u) => {
          const newWindow = window.open(u, '_blank', 'noopener,noreferrer');
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      });
    },
    [message.id, onLinkClick],
  );

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    const isUrl = (s: string) =>
      s.startsWith('http://') || s.startsWith('https://');

    return parts.map((part, index) => {
      if (isUrl(part)) {
        // Same URL can appear twice in one message; index disambiguates without reordering risk.
        const linkKey = `${part}-${index}`;
        return (
          <a
            key={linkKey}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              handleOpenLink(part);
            }}
          >
            {part}
          </a>
        );
      }
      const textKey = `text-${index}`;
      return <span key={textKey}>{part}</span>;
    });
  };

  const messageLines = message.text.split('\n').filter((line) => line.trim());
  const textLines = messageLines.filter((line) => !isImageUrl(line));
  const imageUrls = messageLines.filter((line) => isImageUrl(line));

  const previews = getMessagePreviews(message);
  const hasPreviews = previews.length > 0;
  const strippedTextLines = hasPreviews
    ? textLines
        .map((line) =>
          line.replace(/(https?:\/\/[^\s]+)|(exosfit:[^\s]*)/gi, '').trim(),
        )
        .filter((line) => line.length > 0)
    : textLines;
  const displayText = strippedTextLines.join('\n').trim();
  const linkOnly =
    hasPreviews && textLines.length > 0 && displayText.length === 0;
  const showBubble =
    !linkOnly &&
    (displayText.length > 0 || imageUrls.length > 0 || !hasPreviews);

  return (
    <>
      <MessageItem isCurrentUser={isCurrentUser}>
        {!isCurrentUser && (
          <Avatar
            src={message.user.avatar?.preview || undefined}
            alt={`${message.user.firstName} ${message.user.lastName}`}
            sx={{ width: 28, height: 28 }}
          />
        )}
        <MessageContent
          isCurrentUser={isCurrentUser}
          $hasPreviews={hasPreviews}
        >
          {showBubble && (
            <MessageBubble isCurrentUser={isCurrentUser}>
              {strippedTextLines.length > 0 && (
                <MessageTextBlock>
                  {strippedTextLines.map((line, index) => (
                    <span
                      key={`${
                        // biome-ignore lint/suspicious/noArrayIndexKey: these have no ids
                        index
                      }-${line}`}
                      style={{ display: 'block' }}
                    >
                      {renderTextWithLinks(line)}
                      {index < strippedTextLines.length - 1 && <br />}
                    </span>
                  ))}
                </MessageTextBlock>
              )}
              {imageUrls.length > 0 && (
                <MessageImages>
                  {imageUrls.map((url) => (
                    <MessageImage
                      key={url}
                      src={url}
                      alt="Shared image"
                      onClick={() => setPreviewImage(url)}
                    />
                  ))}
                </MessageImages>
              )}
            </MessageBubble>
          )}
          {hasPreviews && (
            <PreviewsContainer showBubble={showBubble}>
              {previews.map((p) => (
                <LinkPreview
                  key={p.url}
                  preview={p}
                  behavior={getChatPreviewLinkBehavior(p.url)}
                  onOpenLink={handleOpenLink}
                  isCurrentUser={isCurrentUser}
                />
              ))}
            </PreviewsContainer>
          )}
          <MessageTime isCurrentUser={isCurrentUser}>
            {getReadableShort(message.createdAt)}
          </MessageTime>
        </MessageContent>
      </MessageItem>

      <ImagePreviewDialog
        open={!!previewImage}
        imageUrl={previewImage}
        onClose={() => setPreviewImage('')}
      />
    </>
  );
};

// ─── ChatMessages ─────────────────────────────────────────────────────────────

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  loading,
  currentUserId,
  messagesEndRef,
  onLinkClick,
}) => (
  <MessagesContainer>
    {loading && messages.length === 0 ? (
      <LoadingContainer>
        <CircularProgress size={40} />
      </LoadingContainer>
    ) : messages.length === 0 ? (
      <EmptyState>
        <Typography variant="body2" color="textSecondary">
          Start a conversation
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Send a message to begin chatting
        </Typography>
      </EmptyState>
    ) : (
      <>
        {messages.map((message) => (
          <MessageItemComponent
            key={message.id}
            message={message}
            isCurrentUser={message.user?.id === currentUserId}
            onLinkClick={onLinkClick}
          />
        ))}
        <div ref={messagesEndRef} />
      </>
    )}
  </MessagesContainer>
);

export default ChatMessages;
